import { Injectable } from "@angular/core";
import { fromEvent } from "rxjs";

/** Testability layer for the native window API.  Always use this instead of accessing window directly. */
@Injectable({ providedIn: "root" })
export class WindowService {
  /** Use `clicked$` instead of creating numerous click event listeners on `window` throughout the app. */
  public clicked$ = fromEvent(window, "click");

  /** Use `blurred$` instead of creating numerous blur event listeners on `window` throughout the app. */
  public blurred$ = fromEvent(window, "blur");

  /** Use `keydown$` instead of creating numerous keydown event listeners on `window` throughout the app. */
  public keydown$ = fromEvent(window, "keydown");

  /** Use `message$` instead of creating numerous message event listeners on `window` throughout the app. */
  public message$ = fromEvent<MessageEvent>(window, "message");

  /** Use `resize$` instead of creating numerous resize event listeners on `window` throughout the app. */
  public resize$ = fromEvent(window, "resize");

  public locationReload(): void {
    window.location.reload();
  }

  public locationReplace(url: string): void {
    window.location.replace(url);
  }

  public getLocationPathname(): string {
    return window.location.pathname;
  }

  public locationRedirect(url: string): void {
    window.open(url, "_blank");
  }

  /**
   * POSTs an empty form as a top-level navigation. Prefer it over
   * `locationReplace` for a state-changing endpoint: a GET can be triggered as
   * a subresource — a prefetch, an `<img>`, a link scanner — and a cross-site
   * GET navigation still carries `SameSite=Lax` cookies. A POST does neither.
   */
  public submitFormPost(action: string): void {
    const form = document.createElement("form");
    form.method = "post";
    form.action = action;
    document.body.appendChild(form);
    form.submit();
  }

  public getWindowBounds(): { width: number; height: number } {
    return {
      width: window.innerWidth ?? document.documentElement.clientWidth,
      height: window.innerHeight ?? document.documentElement.clientHeight,
    };
  }
}
